import { confirmSignIn, rememberDevice, signIn } from '@aws-amplify/auth';
import { useMsal } from '@azure/msal-react';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import SixCodeTextField from 'components/SixCodeTextField/SixCodeTextField';
import { loginRequest } from 'environment/SsoConfig';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { triggerFetchToken } from 'state/actions/graphqlToken';
import Background from 'ui/atoms/Background/Background';
import Icon from 'ui/atoms/Icon/Icon';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const Login: React.FC<React.PropsWithChildren> = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [confirmCode, setConfirmCode] = useState<string[]>(Array(6).fill(''));
  const [error2MFA, setError2MFA] = useState<boolean>(false);
  const [isConfirmWithAuthenticatorEnabled, setIsConfirmWithAuthenticatorEnabled] = useState<boolean>(false);
  const [isRememberDeviceChecked, setIsRememberDeviceChecked] = useState<boolean>(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const [showPassword, setShowPassword] = useState(false);
  const { translation } = useTranslation();
  const { instance } = useMsal();

  const handleLoginSSO = async () => {
    await instance.loginRedirect(loginRequest);
  };

  const handleLoginEmailPassword = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (email && password) {
      event.preventDefault();
      setError(undefined);
      try {
        const data = await signIn({ username: email, password });
        if (rememberMe) {
          localStorage.setItem('email', email);
        }
        if (data) {
          if (data.isSignedIn === false && data.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE')
            setIsConfirmWithAuthenticatorEnabled(true);
          else triggerFetchToken();
        }
      } catch (err) {
        if (err instanceof Error) {
          const errorName = err.name;
          const errorTypes = ['NotAuthorizedException', 'UserNotFoundException'];
          if (errorTypes.includes(errorName)) {
            setError('Email and password combination incorrect');
            return;
          }
        }
        handleFetchError('' + err);
      }
    }
  };

  const handleConfirmCode = async () => {
    if (confirmCode) {
      try {
        const data = await confirmSignIn({
          challengeResponse: confirmCode.join(''),
          options: isRememberDeviceChecked ? { rememberDevice: true } : undefined
        });
        if (data)
          if (isRememberDeviceChecked) {
            await rememberDevice();
          }
        triggerFetchToken();
      } catch (err) {
        if (err instanceof Error) {
          setError2MFA(true);
        } else handleFetchError('' + err);
      }
    }
  };

  const handleResendCode = async () => {
    if (email) {
      try {
        await signIn({ username: email, password });
      } catch (err) {
        handleFetchError('' + err);
      }
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  React.useEffect(() => {
    const savedEmail = localStorage.getItem('email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <ComponentWrapper width="100vw" height="100vh" alignItems="center" justifyContent="center">
      <Background id="background" withLogo={false} />
      <Paper
        elevation={3}
        data-testid="login-window"
        id="login-window"
        style={{ padding: '40px', width: '448px', borderRadius: '10px' }}
      >
        {isConfirmWithAuthenticatorEnabled ? (
          <ComponentWrapper flexDirection="column" gap="40px">
            <ComponentWrapper justifyContent="space-between" alignItems="center">
              <Typography variant="h4" color="textPrimary">
                ATLAS
              </Typography>
              <Icon name="TKHSecurityLogoDark" width={106} height={40} viewBox="0 0 170 64" />
            </ComponentWrapper>
            <ComponentWrapper flexDirection="column" gap="24px">
              <Typography>{translation.enter_verification_code}</Typography>
              <ComponentWrapper flexDirection="column" gap="16px">
                <SixCodeTextField
                  code={confirmCode}
                  setCode={setConfirmCode}
                  triggerWhenCodeIsFull={handleConfirmCode}
                  setError={setError2MFA}
                />
                {error2MFA && (
                  <Alert
                    variant="standard"
                    severity={'error'}
                    style={{ backgroundColor: 'rgba(211, 47, 47, 0.08)', border: '1px solid #D32F2F' }}
                    sx={{
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      '& .MuiSvgIcon-root': {
                        color: '#F44336'
                      },
                      '& .MuiAlert-message': {
                        color: 'rgba(244, 199, 199, 1)'
                      }
                    }}
                  >
                    <ComponentWrapper flexDirection="column">
                      <Typography color="rgba(244, 199, 199, 1)" variant="h6">
                        {translation.invalid_verification_code}
                      </Typography>
                      <Typography variant="body2" color="rgba(244, 199, 199, 1)">
                        {translation.verification_code_hint}
                      </Typography>
                    </ComponentWrapper>
                  </Alert>
                )}
                <ComponentWrapper alignItems="center" gap="8px">
                  <Checkbox
                    checked={isRememberDeviceChecked}
                    onChange={(event) => setIsRememberDeviceChecked(event.target.checked)}
                  />
                  <Typography>{translation.trust_device_for_30_days}</Typography>
                  <Tooltip
                    elementOnHover={
                      <ComponentWrapper>
                        <ComponentWrapper padding="16px">
                          <Typography variant="body1" width="376px">
                            {translation.trusted_device_hint}
                          </Typography>
                        </ComponentWrapper>
                      </ComponentWrapper>
                    }
                    id="role-tooltip"
                  >
                    <ComponentWrapper cursor="pointer" alignItems="center">
                      <Icon name="Info" id="trust-device-tooltip-icon" />
                    </ComponentWrapper>
                  </Tooltip>
                </ComponentWrapper>
              </ComponentWrapper>
            </ComponentWrapper>
          </ComponentWrapper>
        ) : (
          <ComponentWrapper flexDirection="column" gap="2rem">
            <ComponentWrapper flexDirection="row" justifyContent="space-between">
              <Typography variant="h4">ATLAS</Typography>
              <Icon name="TKHSecurityLogo" width={106} height={40} viewBox="0 0 170 64" />
            </ComponentWrapper>
            <ComponentWrapper flexDirection="column">
              <ComponentWrapper flexDirection="column" gap="1rem">
                <TextField
                  id="username"
                  type="email"
                  label={translation.email_address}
                  size="medium"
                  variant="outlined"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <TextField
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  label={translation.password}
                  size="medium"
                  variant="outlined"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  InputProps={{
                    endAdornment: password ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? (
                            <VisibilityOutlined style={{ color: 'rgba(255, 255, 255, 0.56)' }} />
                          ) : (
                            <VisibilityOffOutlined style={{ color: 'rgba(255, 255, 255, 0.56)' }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ) : undefined
                  }}
                />
                {error && (
                  <Alert
                    variant="standard"
                    severity={'error'}
                    style={{ backgroundColor: 'rgba(211, 47, 47, 0.08)', border: '1px solid #D32F2F' }}
                    sx={{
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      '& .MuiSvgIcon-root': {
                        color: '#F44336'
                      },
                      '& .MuiAlert-message': {
                        color: 'rgba(244, 199, 199, 1)'
                      }
                    }}
                  >
                    <ComponentWrapper flexDirection="column">
                      <Typography variant="body2" color="rgba(244, 199, 199, 1)">
                        {error}
                      </Typography>
                    </ComponentWrapper>
                  </Alert>
                )}
                <ComponentWrapper justifyContent="space-between" alignItems="center">
                  <ComponentWrapper alignItems="center">
                    <Checkbox value={rememberMe} onChange={(event) => setRememberMe(!!event.target.value)} />{' '}
                    <Typography>{translation.remember_me}</Typography>
                  </ComponentWrapper>
                  <Link onClick={handleForgotPassword} style={{ cursor: 'pointer' }}>
                    <Typography
                      variant="body2"
                      alignSelf="center"
                      color="textPrimary"
                      style={{
                        textDecoration: 'underline',
                        fontStyle: 'solid',
                        offset: '0%',
                        textDecorationThickness: '0%'
                      }}
                    >
                      {translation.forgot_your_password}
                    </Typography>
                  </Link>
                </ComponentWrapper>
                <Button
                  onClick={handleLoginEmailPassword}
                  variant="contained"
                  size="large"
                  color="primary"
                  style={{ color: '#373737' }}
                  id="sign-in-button"
                >
                  {translation.sign_in}
                </Button>
              </ComponentWrapper>
              <ComponentWrapper
                flexDirection="row"
                gap="1rem"
                alignItems="center"
                justifyContent="center"
                margin="1rem 0 0 0"
              >
                <Divider style={{ width: '20%', height: '1px' }} />
                <Typography color="secondary" variant="body2">
                  {translation.or_use_sso}
                </Typography>
                <Divider style={{ width: '20%', height: '1px' }} />
              </ComponentWrapper>
              <ComponentWrapper
                flexDirection="column"
                gap="1rem"
                onClick={handleLoginSSO}
                alignSelf="center"
                cursor="pointer"
                width="215px"
                height="41px"
                margin="1rem 0 0 0"
              >
                <Button size="large" color="secondary" variant="outlined">
                  <ComponentWrapper gap="8px">
                    <Icon width="21" height="21" name="MicrosoftSymbol" id="sign-in-microsoft-button" />
                    <Typography color="textPrimary" variant="button">
                      MICROSOFT
                    </Typography>
                  </ComponentWrapper>
                </Button>
              </ComponentWrapper>
            </ComponentWrapper>
          </ComponentWrapper>
        )}
      </Paper>
      <ComponentWrapper style={{ position: 'absolute', bottom: 16, left: 24 }} gap="24px">
        <Typography variant="caption" color="secondary" alignSelf="center" justifyContent="center" textAlign="center">
          Terms of Service
        </Typography>
        <Typography variant="caption" color="secondary" alignSelf="center" justifyContent="center" textAlign="center">
          Privacy
        </Typography>
      </ComponentWrapper>
    </ComponentWrapper>
  );
};

export default Login;
